// Data-driven Project Reporting UI Layout builder.
//
// Loads the real ReportLayout template for a product type (and, for
// Certification, a report type), lets an admin add / remove / reorder widgets
// and choose their side, then saves it back via the /api/report-layout CRUD.
// Because the Project Reporting Hub renders these same templates by widgetKey,
// whatever is designed here is exactly what shows up in the report hub.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, BarChart3, Table as TableIcon, Layout, X, CircleArrowUp, CircleArrowDown,
  CircleArrowLeft, CircleArrowRight, CircleX, Save, Eye, Loader2, AlertCircle, CheckCircle,
} from 'lucide-react';
import { addTemplate, getTemplates, updateTemplate } from '../../services/apiService';
import {
  CATEGORY, CERT_REPORT_TYPES, WIDGET_CATEGORIES, resolveIdentity,
  buildDefaultSections, widgetsForCategory,
} from './reportWidgetCatalog';
import Swal from 'sweetalert2';

const CATEGORY_ICON = { chart: BarChart3, table_matrix: TableIcon, highlight: Layout };

function WidgetIcon({ category }) {
  const Icon = CATEGORY_ICON[category] || Layout;
  return <Icon className="w-6 h-6 text-blue-400" />;
}

const ReportLayoutBuilder = ({ subCategory }) => {
  const isCert = subCategory === 'Certification';

  const [reportType, setReportType] = useState('bcst_daily');
  const templateType = isCert ? reportType : 'default';

  const [templateId, setTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type, text }

  // Add-widget modal state
  const [addOpen, setAddOpen] = useState(false);
  const [addSectionKey, setAddSectionKey] = useState(null);
  const [addTab, setAddTab] = useState('chart');
  const [picked, setPicked] = useState([]); // widgetKeys checked in modal
  const [guideKey, setGuideKey] = useState(null);
  const [addError, setAddError] = useState('');

  const identity = useMemo(() => resolveIdentity(subCategory, templateType), [subCategory, templateType]);

  const flash = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Load template (or fall back to catalog defaults) ───────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTemplates({ category: CATEGORY, subCategory, templateType });
      const doc = Array.isArray(res?.data) ? res.data[0] : res?.data;
      if (doc && doc._id) {
        setTemplateId(doc._id);
        setTemplateName(doc.name || identity.name);
        setSections(normalizeSections(doc.sections || []));
      } else {
        setTemplateId(null);
        setTemplateName(identity.name);
        setSections(buildDefaultSections(subCategory, templateType));
      }
    } catch (e) {
      // On error fall back to defaults so the admin can still design.
      setTemplateId(null);
      setTemplateName(identity.name);
      setSections(buildDefaultSections(subCategory, templateType));
      flash('error', 'Could not load saved layout. Showing default layout.');
    } finally {
      setLoading(false);
    }
  }, [subCategory, templateType, identity.name]);

  useEffect(() => { load(); }, [load]);

  // ── Mutators ───────────────────────────────────────────────────────────────
  const moveSection = (index, dir) => {
    setSections((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      // Header stays pinned at the top.
      if (prev[index].sectionKey === 'header' || prev[target].sectionKey === 'header') return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, position: i }));
    });
  };

  const removeWidget = (sectionKey, widgetKey) => {
    setSections((prev) => prev.map((s) => {
      if (s.sectionKey !== sectionKey) return s;
      const wdg = s.widgets.find((x) => x.widgetKey === widgetKey);
      if (wdg?.locked) {
        flash('error', `"${wdg.widgetName}" is mandatory and cannot be removed.`);
        return s;
      }
      const widgets = s.widgets
        .filter((x) => x.widgetKey !== widgetKey)
        .map((x, i) => ({ ...x, position: i, side: reflowSide(i, s) }));
      return { ...s, widgets };
    }));
  };

  const swapSides = (sectionKey) => {
    setSections((prev) => prev.map((s) => {
      if (s.sectionKey !== sectionKey || s.widgets.length !== 2) return s;
      const widgets = [s.widgets[1], s.widgets[0]].map((x, i) => ({ ...x, position: i, side: i === 0 ? 'left' : 'right' }));
      return { ...s, widgets };
    }));
  };

  const addPickedWidgets = () => {
    setAddError('');
    const section = sections.find((s) => s.sectionKey === addSectionKey);
    if (!section) return;
    const max = section.rules?.maxWidgets;
    const existingKeys = new Set(section.widgets.map((w) => w.widgetKey));
    const toAdd = picked.filter((k) => section.rules?.allowDuplicates || !existingKeys.has(k));

    if (max != null && section.widgets.length + toAdd.length > max) {
      setAddError(`This section allows a maximum of ${max} widget(s).`);
      return;
    }

    const pool = widgetsForCategory(subCategory, addTab, templateType);
    const additions = toAdd
      .map((k) => pool.find((p) => p.widgetKey === k))
      .filter(Boolean);

    setSections((prev) => prev.map((s) => {
      if (s.sectionKey !== addSectionKey) return s;
      let widgets = [...s.widgets, ...additions.map((a) => ({
        widgetKey: a.widgetKey, widgetName: a.widgetName, category: a.category,
        position: 0, side: 'full', subIndex: 0, locked: false, config: {},
      }))];
      widgets = widgets.map((x, i) => ({ ...x, position: i, side: reflowSide(i, { ...s, widgets }) }));
      return { ...s, widgets };
    }));
    closeAdd();
  };

  const openAdd = (section) => {
    const allowed = section.rules?.allowedCategories || [];
    const firstTab = allowed.includes('chart') ? 'chart' : allowed.includes('table_matrix') ? 'table_matrix' : 'highlight';
    setAddSectionKey(section.sectionKey);
    setAddTab(firstTab);
    setPicked([]);
    setGuideKey(null);
    setAddError('');
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setAddSectionKey(null);
    setPicked([]);
    setGuideKey(null);
    setAddError('');
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const save = async (status) => {
  setSaving(true);

  // Check before save whether this is a new template or existing one
  const isExistingTemplate = !!templateId;

  try {
    const payload = {
      name: templateName || identity.name,
      templateType: identity.templateType,
      category: identity.category,
      subCategory: identity.subCategory,
      productType: null,
      isDefault: identity.isDefault,
      status,
      sections: sections.map((s, i) => ({
        ...s,
        position: i,
        widgets: s.widgets.map((wd, wi) => ({
          ...wd,
          position: wi
        })),
      })),
    };
    let res;
    if (templateId) { res = await updateTemplate(templateId, payload);
    } else {
      res = await addTemplate(payload);
    }

    const saved = res?.data;
    if (saved?._id) {
      setTemplateId(saved._id);
      setSections(normalizeSections(saved.sections || sections));
    }

    // SweetAlert messages
    if (status === 'published') {
      Swal.fire({
        icon: 'success',
        title: isExistingTemplate
          ? 'Template Updated!'
          : 'Template Published!',
        text: isExistingTemplate
          ? 'The existing template has been updated successfully.'
          : 'The template has been published successfully.',
        confirmButtonColor: '#16a34a',
      });
    } else {
      Swal.fire({
        icon: 'success',
        title: 'Draft Saved!',
        text: 'Your draft has been saved successfully.',
        confirmButtonColor: '#2563eb',
      });
    }
  } catch (e) {
    const msg = e.response?.data?.message || 'Failed to save template.';

    Swal.fire({
      icon: 'error',
      title: 'Save Failed',
      text: msg,
      confirmButtonColor: '#dc2626',
    });
  } finally {
    setSaving(false);
  }
};

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const addPool = addSectionKey
    ? widgetsForCategory(subCategory, addTab, templateType)
    : [];
  const addSection = sections.find((s) => s.sectionKey === addSectionKey);
  const addAllowedTabs = (addSection?.rules?.allowedCategories || []).filter((c) => WIDGET_CATEGORIES[c]);

  return (
    <div className="w-full">
      <div className="bg-[#0F1217] border border-gray-800 rounded-xl overflow-hidden">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 border-b border-gray-800 px-4 sm:px-6 py-4 bg-[#0A0C10] text-base sm:text-xl font-semibold text-white">
          <span>Project Reporting UI Layout</span>
          <span className="text-gray-500">›</span>
          <span>{subCategory}</span>
        </div>

        {/* Edit Zone bar */}
        <div className="bg-[#8C8C8C] px-4 sm:px-6 py-2 border-b border-gray-700">
          <h2 className="text-black font-bold text-lg">Edit Zone</h2>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {/* Toast */}
          {toast && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 border ${
              toast.type === 'success' ? 'bg-green-900/40 border-green-600 text-green-200'
                : 'bg-red-900/40 border-red-600 text-red-200'}`}>
              {toast.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span className="text-sm">{toast.text}</span>
            </div>
          )}

          {/* Step 1: template / report-type selector */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="text-sm font-medium text-white whitespace-nowrap">
              {isCert ? 'Step 1 : Select a report type' : 'Step 1 : Select a UI template'}
            </div>
            <select
              value={isCert ? reportType : 'default'}
              onChange={(e) => setReportType(e.target.value)}
              disabled={!isCert}
              className="bg-white text-black border border-gray-300 text-sm rounded-md px-4 py-2 focus:outline-none focus:border-blue-500 disabled:opacity-80"
            >
              {isCert
                ? CERT_REPORT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)
                : <option value="default">Default template</option>}
            </select>
            <span className="text-xs text-gray-400">
              {templateId ? 'Editing saved template' : 'New template (not yet saved)'}
            </span>
          </div>

          <div className="text-sm font-medium text-white mb-4">
            Step 2 : Customize the sections below based on the selected template
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <SectionBlock
                key={section.sectionKey}
                section={section}
                index={index}
                total={sections.length}
                subCategory={subCategory}
                onAdd={() => openAdd(section)}
                onRemoveWidget={removeWidget}
                onMoveUp={() => moveSection(index, -1)}
                onMoveDown={() => moveSection(index, 1)}
                onSwap={() => swapSides(section.sectionKey)}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              disabled={saving}
              onClick={() => save('draft')}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-white text-sm w-full sm:w-auto transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save as Draft
            </button>
            <button
              disabled={saving}
              onClick={() => save('published')}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg text-white font-medium text-sm w-full sm:w-auto transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Publish
            </button>
          </div>
        </div>
      </div>

      {/* Add Widget modal */}
      {addOpen && (
        <AddWidgetModal
          subCategory={subCategory}
          tabs={addAllowedTabs}
          activeTab={addTab}
          onTab={(t) => { setAddTab(t); setPicked([]); setGuideKey(null); setAddError(''); }}
          pool={addPool}
          picked={picked}
          onToggle={(k) => setPicked((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k])}
          guideKey={guideKey}
          onGuide={setGuideKey}
          error={addError}
          onCancel={closeAdd}
          onConfirm={addPickedWidgets}
        />
      )}
    </div>
  );
};

// ── Section block ─────────────────────────────────────────────────────────────
function SectionBlock({ section, index, total, subCategory, onAdd, onRemoveWidget, onMoveUp, onMoveDown, onSwap }) {
  const isHeader = section.sectionKey === 'header';
  const canAdd = section.editable && (section.rules?.maxWidgets == null || section.widgets.length < section.rules.maxWidgets);

  if (isHeader) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-white mb-3">#{index + 1}. {section.sectionName}</h3>
        <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-6 text-center text-gray-400">
          Predefined by report type. Not editable.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm sm:text-base font-semibold text-white">#{index + 1}. {section.sectionName}</h3>
          <div className="flex items-center gap-1">
            <CircleArrowUp
              onClick={onMoveUp}
              className={`w-5 h-5 ${index <= 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 cursor-pointer hover:text-blue-600'}`}
            />
            <CircleArrowDown
              onClick={onMoveDown}
              className={`w-5 h-5 ${index >= total - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-blue-400 cursor-pointer hover:text-blue-600'}`}
            />
          </div>
        </div>
        {canAdd && (
          <button
            onClick={onAdd}
            className="flex items-center justify-center gap-2 bg-violet-500 hover:bg-violet-600 px-3 py-2 rounded-lg text-xs sm:text-sm text-white transition-colors w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" /> Add Widget
          </button>
        )}
      </div>

      <div className="bg-[#1A1F2A] border border-gray-700 rounded-xl p-3 sm:p-6">
        {section.widgets.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-6">No widgets yet. Use “Add Widget”.</div>
        ) : (
          <div className={`grid gap-3 ${section.widgets.length > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
            {section.widgets.map((wdg, wi) => (
              <div key={wdg.widgetKey} className="bg-white border border-gray-300 rounded-xl p-2 sm:p-3">
                <div className="flex items-center justify-between gap-2 sm:gap-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <WidgetIcon category={wdg.category} />
                    <span className="text-sm sm:text-base font-bold text-black truncate" title={wdg.widgetName}>
                      {wdg.widgetName}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {section.widgets.length === 2 && (
                      wi === 0
                        ? <CircleArrowRight onClick={onSwap} className="w-5 h-5 text-blue-400 cursor-pointer hover:text-blue-600" title="Swap sides" />
                        : <CircleArrowLeft onClick={onSwap} className="w-5 h-5 text-blue-400 cursor-pointer hover:text-blue-600" title="Swap sides" />
                    )}
                    {wdg.locked ? (
                      <span className="text-[10px] uppercase tracking-wide text-gray-400 px-1">Locked</span>
                    ) : (
                      <CircleX
                        onClick={() => onRemoveWidget(section.sectionKey, wdg.widgetKey)}
                        className="w-5 h-5 text-red-400 cursor-pointer hover:text-red-600"
                        title="Remove widget"
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add Widget modal ───────────────────────────────────────────────────────────
function AddWidgetModal({ subCategory, tabs, activeTab, onTab, pool, picked, onToggle, guideKey, onGuide, error, onCancel, onConfirm }) {
  const guide = guideKey ? pool.find((p) => p.widgetKey === guideKey)?.guide : (pool[0]?.guide || null);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0F1B2D] border border-gray-700 rounded-lg w-full max-w-3xl shadow-2xl">
        {/* Title bar */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#102A4C] rounded-t-lg">
          <h3 className="text-white font-semibold">Add Widget</h3>
          <X className="w-5 h-5 text-gray-300 cursor-pointer hover:text-white" onClick={onCancel} />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {tabs.map((t) => (
            <button
              key={t}
              onClick={() => onTab(t)}
              className={`px-5 py-2 text-sm font-medium ${activeTab === t ? 'bg-[#1B3A63] text-white' : 'text-gray-300 hover:text-white'}`}
            >
              {WIDGET_CATEGORIES[t]}
            </button>
          ))}
        </div>

        {/* Body: widget list + guide */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
          <div className="space-y-2">
            {pool.length === 0 && <div className="text-gray-400 text-sm">No widgets available.</div>}
            {pool.map((wdg) => (
              <label
                key={wdg.widgetKey}
                onMouseEnter={() => onGuide(wdg.widgetKey)}
                className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-gray-700 rounded px-3 py-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={picked.includes(wdg.widgetKey)}
                  onChange={() => onToggle(wdg.widgetKey)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-white">{wdg.widgetName}</span>
              </label>
            ))}
          </div>

          <div className="bg-white rounded p-4 text-sm text-gray-800">
            <div className="font-semibold underline mb-2">Widget guide</div>
            {guide ? (
              <ul className="space-y-1">
                <li><span className="font-semibold">Allowed section:</span> {guide.allowedSection}</li>
                <li><span className="font-semibold">Multi-select:</span> {guide.multiSelect}</li>
                <li><span className="font-semibold">Data source:</span> {guide.dataSource}</li>
                <li><span className="font-semibold">Output:</span> {guide.output}</li>
                <li><span className="font-semibold">Usage:</span> {guide.usage}</li>
                {guide.notes ? <li><span className="font-semibold">Notes/Limit:</span> {guide.notes}</li> : null}
              </ul>
            ) : <div className="text-gray-500">Hover a widget to see its rules.</div>}
          </div>
        </div>

        {error && <div className="px-5 pb-2 text-sm text-red-400">{error}</div>}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-700">
          <button onClick={onCancel} className="px-4 py-2 rounded bg-gray-600 hover:bg-gray-500 text-white text-sm">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={picked.length === 0}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm"
          >
            Add Selected
          </button>
        </div>
      </div>
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────
// Recompute a widget's side based on its index + section size.
function reflowSide(index, section) {
  const count = section.widgets ? section.widgets.length : 0;
  if (count <= 1) return 'full';
  return index === 0 ? 'left' : 'right';
}

// Ensure sections/widgets from the API have all fields the builder expects.
function normalizeSections(secs) {
  return (secs || [])
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((s, i) => ({
      sectionKey: s.sectionKey,
      sectionName: s.sectionName,
      position: i,
      editable: s.editable !== false,
      rules: {
        maxWidgets: s.rules?.maxWidgets ?? null,
        allowedCategories: s.rules?.allowedCategories || [],
        allowDuplicates: !!s.rules?.allowDuplicates,
        mandatory: !!s.rules?.mandatory,
      },
      widgets: (s.widgets || [])
        .slice()
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((wd, wi) => ({
          widgetKey: wd.widgetKey,
          widgetName: wd.widgetName,
          category: wd.category,
          position: wi,
          side: ['left', 'right', 'full'].includes(wd.side) ? wd.side : 'full',
          subIndex: wd.subIndex || 0,
          locked: !!wd.locked,
          config: wd.config || {},
        })),
    }));
}

export default ReportLayoutBuilder;
